import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  ChevronRight, 
  ArrowRight,
  AlertTriangle,
  BookOpen,
  Clock,
  Calendar,
  Target,
  Info,
  FileText,
  MapPin,
  Layers,
  Scale,
  Home,
  Building2,
  User,
  Mail,
  Phone,
  Check
} from 'lucide-react';

// Step indicator component - moved outside to avoid re-renders
const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {[1, 2, 3].map((step, index) => (
      <React.Fragment key={step}>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
          currentStep >= step 
            ? 'bg-[#635BFF] text-white' 
            : 'bg-gray-200 text-gray-500'
        }`}>
          {currentStep > step ? <Check className="w-5 h-5" /> : step}
        </div>
        {index < 2 && (
          <div className={`w-16 h-1 mx-2 rounded transition-all ${
            currentStep > step ? 'bg-[#635BFF]' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const PrixEtudeSol = () => {
  // Configuration des prix de base HT (ordres de grandeur 2026)
  const PRIX = {
    g1: { bas: 800, haut: 1500, surfaceSensible: false, txt: "G1 — étude préalable obligatoire pour la vente d'un terrain en zone argileuse (loi ELAN), à la charge du vendeur." },
    g2avp: { bas: 1500, haut: 2500, surfaceSensible: true, txt: "G2 AVP — dimensionnement des principes de fondation, à la charge de celui qui construit." },
    g2pro: { bas: 2500, haut: 3600, surfaceSensible: true, txt: "G2 PRO — dimensionnement détaillé et plans d'exécution." },
    g3: { bas: 2000, haut: 5000, surfaceSensible: true, txt: "G3 — étude et suivi géotechnique d'exécution, réalisée par l'entreprise." },
    g4: { bas: 2000, haut: 4000, surfaceSensible: false, txt: "G4 — supervision géotechnique de l'exécution, contrôle du chantier." },
    g5: { bas: 1600, haut: 4000, surfaceSensible: false, txt: "G5 — diagnostic d'un ouvrage existant (fissures, sinistre, expertise)." }
  };

  const SURFACE = {
    refM2: 150,
    eurosParM2Min: 1.5,
    eurosParM2Max: 2.5,
    supplementMax: 1.8
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Technical
    mission: 'g2avp',
    surface: '100',
    // Step 2 - Context
    complexite: '1.0',
    delai: '1.0',
    codePostal: '',
    // Step 3 - Contact
    nom: '',
    email: '',
    telephone: ''
  });
  const [result, setResult] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const sections = [
    { id: 'section-outil', title: "Simulation gratuite" },
    { id: 'section-missions', title: "Comprendre G1 à G5" },
    { id: 'section-loi-elan', title: "Loi ELAN" },
    { id: 'section-facteurs', title: "Facteurs de prix" },
    { id: 'section-guides', title: "Pour aller plus loin" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const formatEuros = (n) => {
    const rounded = Math.round(n / 50) * 50;
    return rounded.toLocaleString('fr-FR') + ' €';
  };

  const calculate = () => {
    const base = PRIX[formData.mission];
    const complexite = parseFloat(formData.complexite);
    const delai = parseFloat(formData.delai);

    let bas = base.bas;
    let haut = base.haut;

    if (base.surfaceSensible) {
      const s = parseFloat(formData.surface) || SURFACE.refM2;
      const exces = Math.max(0, s - SURFACE.refM2);
      let addMin = exces * SURFACE.eurosParM2Min;
      let addMax = exces * SURFACE.eurosParM2Max;
      addMin = Math.min(addMin, base.bas * (SURFACE.supplementMax - 1));
      addMax = Math.min(addMax, base.haut * (SURFACE.supplementMax - 1));
      bas += addMin;
      haut += addMax;
    }

    const k = complexite * delai;
    bas *= k;
    haut *= k;

    setResult({
      bas,
      haut,
      missionInfo: base.txt
    });
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.mission) newErrors.mission = 'Sélectionnez une mission';
    if (PRIX[formData.mission]?.surfaceSensible && (!formData.surface || parseFloat(formData.surface) < 10)) {
      newErrors.surface = 'Entrez une surface valide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.codePostal || formData.codePostal.length < 4) {
      newErrors.codePostal = 'Entrez un code postal valide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Entrez votre nom';
    if (!formData.email.trim() || !formData.email.includes('@')) newErrors.email = 'Entrez un email valide';
    if (!formData.telephone.trim() || formData.telephone.length < 10) newErrors.telephone = 'Entrez un téléphone valide';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      calculate();
      setCurrentStep(3);
    }
  };

  const handleSubmit = async () => {
    if (validateStep3()) {
      setIsSubmitting(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const formPayload = new FormData();
        formPayload.append('nom', formData.nom);
        formPayload.append('telephone', formData.telephone);
        formPayload.append('email', formData.email);
        formPayload.append('code_postal', formData.codePostal);
        formPayload.append('mission', formData.mission);
        formPayload.append('surface', formData.surface || '');
        formPayload.append('complexite', formData.complexite);
        formPayload.append('delai', formData.delai);
        if (result) {
          formPayload.append('estimation_bas', result.bas);
          formPayload.append('estimation_haut', result.haut);
        }
        formPayload.append('rgpd_accepted', 'true');

        const response = await fetch(`${API_URL}/api/demandes/estimation-etude-sol`, {
          method: 'POST',
          body: formPayload
        });

        if (response.ok) {
          setSubmitSuccess(true);
          setTimeout(() => {
            document.getElementById('result-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          console.error('Erreur lors de la soumission');
        }
      } catch (error) {
        console.error('Erreur réseau:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const showSurfaceField = PRIX[formData.mission]?.surfaceSensible;

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Estimateur prix étude de sol G1 à G5",
    "description": "Outil d'estimation du prix d'une étude de sol géotechnique selon le type de mission (G1 à G5), la surface et la complexité du terrain.",
    "url": "https://geots.consulting/calcul/prix-etude-de-sol",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quel est le prix d'une étude de sol G1 ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Une étude de sol G1 (étude préalable) coûte généralement entre 800 € et 1 500 € HT. Cette étude est obligatoire depuis la loi ELAN pour la vente d'un terrain en zone argileuse."
        }
      },
      {
        "@type": "Question",
        "name": "Quel est le prix d'une étude de sol G2 ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Une étude de sol G2 AVP (avant-projet) coûte entre 1 500 € et 2 500 € HT pour une maison standard. Une G2 PRO (projet) coûte entre 2 500 € et 3 600 € HT. Le prix varie selon la surface et la complexité du terrain."
        }
      },
      {
        "@type": "Question",
        "name": "L'étude de sol est-elle obligatoire ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Depuis la loi ELAN (applicable au 1er octobre 2020), l'étude G1 est obligatoire pour vendre un terrain à bâtir en zone d'exposition moyenne ou forte au retrait-gonflement des argiles. Cela concerne près de la moitié du territoire français."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Estimez le coût de votre étude de sol en 2 minutes | GEOTS</title>
        <meta name="description" content="Estimez gratuitement le prix d'une étude de sol géotechnique (G1, G2, G3, G4, G5). Fourchettes de prix 2026, loi ELAN, facteurs de variation." />
        <link rel="canonical" href="https://geots.consulting/calcul/prix-etude-de-sol" />
        <meta property="og:title" content="Estimez le coût de votre étude de sol en 2 minutes | GEOTS" />
        <meta property="og:description" content="Estimez le prix d'une étude de sol selon le type de mission, la surface et la complexité du terrain." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/calcul/prix-etude-de-sol" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/calcul" className="hover:text-[#635BFF]">Calculs</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Prix étude de sol</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  OUTIL INTERACTIF
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 26 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  ~2 min d&apos;utilisation
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Estimez le coût de votre étude de sol
                <span className="text-[#00D4FF]"> en 2 minutes</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Obtenez une fourchette indicative selon le type de mission géotechnique, 
                la surface de votre projet et la configuration de votre terrain.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('section-outil')}
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  <Calculator className="w-5 h-5" />
                  Estimer le prix
                </button>
                <Link 
                  to="/etude-geotechnique" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander un devis
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-start gap-2 text-amber-700 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Estimation indicative. Seul un devis fait foi.</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              
              {/* Section 1 - L'outil */}
              <section id="section-outil" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                  Commencez votre simulation gratuite
                </h2>

                {/* Multi-step Calculator Card */}
                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-6">
                  
                  {/* Step Indicator */}
                  <StepIndicator currentStep={currentStep} />

                  {/* Step Labels */}
                  <div className="flex justify-between text-xs text-gray-500 mb-8 px-2">
                    <span className={currentStep >= 1 ? 'text-[#635BFF] font-medium' : ''}>Votre projet</span>
                    <span className={currentStep >= 2 ? 'text-[#635BFF] font-medium' : ''}>Localisation</span>
                    <span className={currentStep >= 3 ? 'text-[#635BFF] font-medium' : ''}>Vos coordonnées</span>
                  </div>

                  {/* Step 1: Technical Info */}
                  {currentStep === 1 && (
                    <div className="space-y-5">
                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">
                          Type de mission géotechnique
                          <span className="font-normal text-gray-500 text-sm ml-2">(norme NF P 94-500)</span>
                        </label>
                        <select
                          name="mission"
                          value={formData.mission}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.mission ? 'border-red-500' : 'border-gray-200'}`}
                        >
                          <option value="g1">G1 — préalable (vente de terrain, loi ELAN)</option>
                          <option value="g2avp">G2 AVP — avant-projet (dimensionnement des fondations)</option>
                          <option value="g2pro">G2 PRO — projet (dimensionnement détaillé)</option>
                          <option value="g3">G3 — étude et suivi d&apos;exécution</option>
                          <option value="g4">G4 — supervision d&apos;exécution</option>
                          <option value="g5">G5 — diagnostic (fissures, sinistre, expertise)</option>
                        </select>
                        {errors.mission && <p className="text-red-500 text-sm mt-1">{errors.mission}</p>}
                      </div>

                      {showSurfaceField && (
                        <div>
                          <label className="block font-semibold text-[#0A2540] mb-2">
                            Surface au sol du projet
                            <span className="font-normal text-gray-500 text-sm ml-2">(m²)</span>
                          </label>
                          <input
                            type="number"
                            name="surface"
                            value={formData.surface}
                            onChange={handleChange}
                            placeholder="ex. 150"
                            min="10"
                            max="5000"
                            step="10"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.surface ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {errors.surface && <p className="text-red-500 text-sm mt-1">{errors.surface}</p>}
                        </div>
                      )}

                      <button
                        onClick={handleNextStep}
                        className="w-full bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        Suivant
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {/* Step 2: Context */}
                  {currentStep === 2 && (
                    <div className="space-y-5">
                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">Configuration du terrain</label>
                        <select
                          name="complexite"
                          value={formData.complexite}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                        >
                          <option value="1.0">Terrain plat, accès facile</option>
                          <option value="1.25">Terrain en pente ou accès étroit (mini-foreuse)</option>
                          <option value="1.4">Sous-sol rocheux ou zone de carrières / RGA marquée</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">Délai souhaité</label>
                        <select
                          name="delai"
                          value={formData.delai}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                        >
                          <option value="1.0">Normal (~3 semaines)</option>
                          <option value="1.2">Urgent / express</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Code postal du terrain
                        </label>
                        <input
                          type="text"
                          name="codePostal"
                          value={formData.codePostal}
                          onChange={handleChange}
                          placeholder="ex. 75001"
                          maxLength={5}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.codePostal ? 'border-red-500' : 'border-gray-200'}`}
                        />
                        {errors.codePostal && <p className="text-red-500 text-sm mt-1">{errors.codePostal}</p>}
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setCurrentStep(1)}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                        >
                          Retour
                        </button>
                        <button
                          onClick={handleNextStep}
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                          Calculer mon estimation
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Contact Info + Results */}
                  {currentStep === 3 && (
                    <div className="space-y-5">
                      {/* Contact Fields */}
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <p className="text-sm text-gray-600 mb-4">
                          <Info className="w-4 h-4 inline mr-1 text-[#635BFF]" />
                          Entrez vos coordonnées pour recevoir votre estimation détaillée
                        </p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <User className="w-4 h-4 inline mr-1" />
                              Votre nom
                            </label>
                            <input
                              type="text"
                              name="nom"
                              value={formData.nom}
                              onChange={handleChange}
                              placeholder="Jean Dupont"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.nom ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
                          </div>

                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <Mail className="w-4 h-4 inline mr-1" />
                              Votre email
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="jean@exemple.fr"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                          </div>

                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <Phone className="w-4 h-4 inline mr-1" />
                              Votre téléphone
                            </label>
                            <input
                              type="tel"
                              name="telephone"
                              value={formData.telephone}
                              onChange={handleChange}
                              placeholder="06 12 34 56 78"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.telephone ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.telephone && <p className="text-red-500 text-sm mt-1">{errors.telephone}</p>}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setCurrentStep(2)}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                        >
                          Retour
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Calculator className="w-5 h-5" />
                              Obtenir mon estimation budgétaire
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Result Section - Only shown after Step 3 validation and successful submission */}
                {result && currentStep === 3 && submitSuccess && (
                  <div id="result-box" className="bg-white rounded-2xl shadow-sm border overflow-hidden mb-6">
                    <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] p-4">
                      <h3 className="font-bold text-white text-lg">Votre estimation budgétaire</h3>
                    </div>
                    <div className="p-6">
                      
                      {/* Fourchette */}
                      <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6 text-center mb-4">
                        <div className="text-sm text-gray-500 uppercase tracking-wide mb-2">Fourchette de prix indicative (HT)</div>
                        <div className="text-3xl font-bold text-[#635BFF]">
                          {formatEuros(result.bas)} – {formatEuros(result.haut)} HT
                        </div>
                        <div className="text-sm text-gray-500 mt-2">Estimation indicative — le prix exact dépend de votre parcelle</div>
                      </div>

                      {/* Mission info */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-blue-800">{result.missionInfo}</p>
                        </div>
                      </div>

                      {/* CTA */}
                      <Link
                        to="/etude-geotechnique"
                        className="block w-full bg-[#0A2540] hover:bg-[#1a3a5c] text-white text-center font-semibold py-4 px-6 rounded-xl transition-colors"
                      >
                        Demander un devis personnalisé →
                      </Link>

                      {/* Warning */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <strong className="text-amber-700">Fourchette indicative.</strong>
                            <p className="mt-1">
                              Ces prix sont des ordres de grandeur constatés sur le marché français en 2026, 
                              hors déplacement longue distance et prestations complémentaires.
                              <strong> Seul un devis établi par un bureau d&apos;études qualifié fait foi.</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* Section 2 - Comprendre les missions */}
              <section id="section-missions" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                  Comprendre les missions G1 à G5
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                  <p className="text-gray-600 mb-6">
                    La norme <strong>NF P 94-500</strong> définit les différentes missions d&apos;ingénierie géotechnique. 
                    Chaque mission répond à un besoin spécifique selon l&apos;avancement de votre projet.
                  </p>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">G1</div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">Étude préalable</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            À la charge du <strong>vendeur</strong> d&apos;un terrain en zone argileuse (loi ELAN). 
                            Étude informative, non contractuelle. <strong>Prix : 800 – 1 500 € HT</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-purple-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">G2</div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">Étude de conception (AVP / PRO)</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            À la charge de celui qui <strong>construit</strong>. Dimensionne les fondations. 
                            Indispensable avant chantier. <strong>Prix AVP : 1 500 – 2 500 € HT / PRO : 2 500 – 3 600 € HT</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">G3</div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">Étude et suivi d&apos;exécution</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Réalisée par l&apos;entreprise pendant les travaux. 
                            <strong> Prix : 2 000 – 5 000 € HT</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-amber-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">G4</div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">Supervision d&apos;exécution</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Contrôle du chantier par le maître d&apos;ouvrage.
                            <strong> Prix : 2 000 – 4 000 € HT</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-red-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">G5</div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">Diagnostic</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Diagnostic d&apos;un ouvrage existant : fissures, sinistre, expertise.
                            <strong> Prix : 1 600 – 4 000 € HT</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3 - Loi ELAN */}
              <section id="section-loi-elan" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                  Loi ELAN : l&apos;étude de sol obligatoire
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Scale className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-blue-800">Depuis le 1er octobre 2020</strong>
                        <p className="text-sm text-blue-700 mt-1">
                          La loi ELAN rend l&apos;étude de sol G1 obligatoire pour vendre un terrain à bâtir 
                          en zone d&apos;exposition moyenne ou forte au retrait-gonflement des argiles.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Zones concernées</strong>
                        <p className="text-gray-600 text-sm">
                          Près de la moitié du territoire français est concerné. La couverture s&apos;étendra 
                          davantage à partir de juillet 2026.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Home className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Qui paie l&apos;étude G1 ?</strong>
                        <p className="text-gray-600 text-sm">
                          Le <strong>vendeur</strong> du terrain doit fournir l&apos;étude G1 à l&apos;acheteur.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Qui paie l&apos;étude G2 ?</strong>
                        <p className="text-gray-600 text-sm">
                          Celui qui <strong>construit</strong> (maître d&apos;ouvrage) doit faire réaliser l&apos;étude G2 
                          pour dimensionner les fondations.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800">
                      <strong>💡 Conseil :</strong> Vérifiez si votre terrain est en zone argileuse sur 
                      <a href="https://www.georisques.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] underline ml-1">georisques.gouv.fr</a>
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4 - Facteurs de prix */}
              <section id="section-facteurs" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                  Facteurs de variation du prix
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                  <p className="text-gray-600 mb-6">
                    Le prix d&apos;une étude de sol peut varier significativement selon plusieurs facteurs :
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-[#635BFF]" />
                        <strong className="text-[#0A2540]">Nature du sol</strong>
                      </div>
                      <p className="text-sm text-gray-600">
                        Sol rocheux, zone de carrières, argiles gonflantes = sondages plus complexes.
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-5 h-5 text-[#635BFF]" />
                        <strong className="text-[#0A2540]">Accessibilité</strong>
                      </div>
                      <p className="text-sm text-gray-600">
                        Terrain en pente, accès étroit nécessitant une mini-foreuse.
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-[#635BFF]" />
                        <strong className="text-[#0A2540]">Nombre de sondages</strong>
                      </div>
                      <p className="text-sm text-gray-600">
                        Plus le projet est grand, plus il faut de points de sondage.
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-[#635BFF]" />
                        <strong className="text-[#0A2540]">Délai demandé</strong>
                      </div>
                      <p className="text-sm text-gray-600">
                        Une étude urgente coûte 15-20% plus cher qu&apos;un délai normal.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-[#0A2540] text-white rounded-xl">
                    <p className="font-semibold mb-2">Besoin d&apos;un devis précis ?</p>
                    <p className="text-gray-300 text-sm mb-4">
                      Un bureau d&apos;études qualifié analysera votre projet et vous fournira un devis adapté 
                      à votre terrain et vos besoins spécifiques.
                    </p>
                    <Link
                      to="/etude-geotechnique"
                      className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248e5] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Demander un devis
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Section 5 - Guides connexes */}
              <section id="section-guides" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                  Pour aller plus loin
                </h2>

                <div className="space-y-4">
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-500 p-3 rounded-xl">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Comment choisir une étude géotechnique</p>
                          <p className="text-sm text-gray-600">Guide complet pour comprendre les missions G1 à G5</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                  
                  <Link 
                    to="/guides/fissures-maison"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-red-500 p-3 rounded-xl">
                          <Home className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Fissures dans une maison : causes et solutions</p>
                          <p className="text-sm text-gray-600">Diagnostic G5 et étude de sol après sinistre</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>

                  <Link 
                    to="/etude-geotechnique"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#635BFF] p-3 rounded-xl">
                          <Calculator className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Demander une étude géotechnique</p>
                          <p className="text-sm text-gray-600">Obtenez un devis personnalisé pour votre projet</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                </div>
              </section>

              {/* Final CTA */}
              <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h2 className="text-2xl font-bold mb-3">Besoin d&apos;une étude de sol ?</h2>
                <p className="text-white/80 mb-6 max-w-xl mx-auto">
                  GEOTS vous met en relation avec des bureaux d&apos;études géotechniques qualifiés 
                  pour obtenir un devis adapté à votre projet.
                </p>
                <Link 
                  to="/etude-geotechnique"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#635BFF] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                >
                  <Calculator className="w-5 h-5" />
                  Demander un devis gratuit
                </Link>
              </section>

            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrixEtudeSol;
